<template>
  <div
    v-if="loading === true"
    class="relative rounded-lg align-top mr-3 mb-3 text-80"
    :class="photoContainerClass"
  >
    {{ $t('Resizing photo...') }}
  </div>
  <div
    v-else
    class="relative rounded-lg align-top mr-3 mb-3"
    :class="photoContainerClass"
    :style="{
            'max-width': image === null && drawing === null ? '100%' : null,
            width: image === null && drawing === null ? width + 'px' : null
        }"
  >
    <img
      v-if="image === null && drawing === null"
      class="drawable-photo rounded-lg border border-70"
      :style="{
                'padding-top': '50%',
                width: width + 'px'
            }"
    >

    <img
      v-else
      ref="img"
      class="drawable-photo rounded-lg"
      :class="{
                'border border-60': image === null
            }"
      :src="drawing === null ? imageBlobURL : drawingBlobURL"
      :style="{
                'background-image': drawing ? `url(${imageBlobURL})` : null
            }"
      @click="start"
    >

    <button
      v-if="allowDrawing"
      type="button"
      class="drawing-button btn text-white rounded-lg absolute inline-block"
      @click="start"
    >
      <DrawIcon />
    </button>

    <button
      v-if="canBeDeleted"
      type="button"
      class="delete-drawing-button btn text-white rounded-lg absolute inline-block"
      @click.stop="deletePhoto"
    >
      <DeleteIcon />
    </button>

    <portal to="modals">
      <DrawScreen
        v-if="drawingInProgress"
        :class="photoClass"
        :image="image"
        :drawing="drawing"
        :width="width"
        :height="height"
        :colors="colors"
        :title="title"
        :remove-button-title="removeButtonTitle"
        :allow-drawing="allowDrawing"
        @cancel="cancel"
        @change="change"
      />
    </portal>
  </div>
</template>

<script>
  import DrawScreen from '@/_legacy/components/DrawablePhoto/DrawScreen.vue';
  import DrawIcon from '@/_legacy/components/Icons/DrawIcon.vue';
  import DeleteIcon from '@/_legacy/components/Icons/DeleteIcon.vue';
  import { dataURLtoBlob } from '@/_legacy/util/blob';

  export default {
    components: {
      DrawScreen,
      DrawIcon,
      DeleteIcon,
    },
    props: {
      image: {
        type: String,
        default: null,
      },
      drawing: {
        type: String,
        default: null,
      },
      thumbnail: {
        type: String,
        default: null,
      },
      allowDrawing: {
        type: Boolean,
        default: true,
      },
      title: {
        type: String,
        default: null,
      },
      removeButtonTitle: {
        type: String,
        default: null,
      },
      canBeDeleted: {
        type: Boolean,
        default: true,
      },
      photoClass: {
        type: String,
        default: '',
      },
      showThumbnail: {
        type: Boolean,
        default: true,
      },
      colors: {
        type: Array,
        default: undefined,
      },
      width: {
        type: Number,
        default: null,
      },
      height: {
        type: Number,
        default: null,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      small: {
        type: Boolean,
        default: false,
      },
    },
    data: () => ({
      drawingInProgress: false,
      imageBlobURL: null,
      drawingBlobURL: null,
    }),
    computed: {
      photoContainerClass() {
        return {
          'inline-flex drawable-photo-thumbnail bg-50 items-center justify-center': this
            .showThumbnail,
          'inline-block': !this.showThumbnail,
          'drawable-photo-thumbnail--small': this.small,
        };
      },
    },
    watch: {
      image: {
        handler(image) {
          if (image && !this.thumbnail) {
            if (this.imageBlobURL) {
              URL.revokeObjectURL(this.imageBlobURL);
            }

            this.imageBlobURL = URL.createObjectURL(
              dataURLtoBlob(image),
            );
          }
        },
        immediate: true,
      },
      thumbnail: {
        handler(thumbnail) {
          if (thumbnail) {
            if (this.drawingBlobURL) {
              URL.revokeObjectURL(this.drawingBlobURL);
            }

            this.imageBlobURL = URL.createObjectURL(
              dataURLtoBlob(thumbnail),
            );
          }
        },
        immediate: true,
      },
      drawing: {
        handler(drawing) {
          if (drawing) {
            this.drawingBlobURL = URL.createObjectURL(
              dataURLtoBlob(drawing),
            );
          }
        },
        immediate: true,
      },
    },
    beforeDestroy() {
      if (this.imageBlobURL) {
        URL.revokeObjectURL(this.imageBlobURL);
      }

      if (this.drawingBlobURL) {
        URL.revokeObjectURL(this.drawingBlobURL);
      }
    },
    methods: {
      start() {
        this.drawingInProgress = true;
      },
      change(drawing) {
        this.$emit('change', drawing);
        this.cancel();
      },
      cancel() {
        this.drawingInProgress = false;
      },
      deletePhoto() {
        this.$emit('delete');
      },
    },
  };
</script>

<style scoped>
.drawable-photo {
  max-width: 100%;
  max-height: 100%;
  background-repeat: no-repeat;
  background-size: 100% 100%;
  display: flex;
  position: relative;
  overflow: hidden;
  cursor: pointer;
}

.drawable-photo-thumbnail {
  width: 240px;
  height: 180px;
  display: flex;
  position: relative;
  overflow: hidden;
}

.drawable-photo-thumbnail--small {
  width: 160px;
  height: 100px;
}

.drawable-photo-thumbnail--small > button {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drawable-photo-thumbnail--small > button > svg {
  width: 18px;
  height: 18px;
}

.drawing-button {
  background-color: rgba(0, 0, 0, 0.5);
  left: 0.5rem;
  top: 0.5rem;
  width: 44px;
  height: 44px;
}

.delete-drawing-button {
  background-color: rgba(0, 0, 0, 0.5);
  right: 0.5rem;
  top: 0.5rem;
  width: 44px;
  height: 44px;
}
</style>
